import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box/Box';
import TwInitialsIndicator from '../../TwInitialsIndicator/TwInitialsIndicator';
import { TwButton, TwButtonType } from '../../TwButtons/TwButtons';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import StringUtils from '../../../utils/stringUtils';
import TwLoadMenu from '../../TwLoadMenu/TwLoadMenu';

import useAuth from '../../../providers/Auth/Auth.provider';
import { USER_ROLE } from '../../../providers/User/User.model';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import Stack from '@mui/material/Stack';
import TwWarningIcon from '../../TwWarningIcon/TwWarningIcon';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';
import DetailFile from '../../TwLoadDetail/DetailFile/DetailFile';
import TwScanner from '../../TwScanner/TwScanner';
import useNotifications from '../../../providers/Notifications/Notifications.provider';

import EditFile from '../../TwLoadDetail/EditFiles/EditFiles';
import LoadsService from '../../../providers/Loads/Loads.service';
import TwDocumentViewer, { TwDocumentType } from '../../TwDocumentViewer/TwDocumentViewer';
import { InvoiceResponseV3, InvoiceSources } from '../../../providers/Invoices/Invoices.model';
import { ClientResponse } from '../../../redux/api/customer/customer.model';
import RouteInfo from '../../TwLoadDetail/components/RouteInfo/RouteInfo';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setOrderId } from '../../../redux/features/orders/ordersSlice';
import { logRocketTrack } from '../../../utils/logRocketUtils';
import { TwLoadCommodityReadOnly } from '../../Commodities/TwLoadCommodities/TwLoadCommodities';
import TwGenerateDocuments from '../../TwLoadDetail/components/GenerateRateCon/GenerateRateCon';
import {
  TwLoadDetailTab,
  TwLoadDetailTabsGroup
} from '../../TwLoadDetail/components/LoadTabs/TwLoadDetailTabs';
import Chip from '@mui/material/Chip';
import TwDispatchNotes from '../../TwDispatchNotes/TwDispatchNotes';
import useScrollToView from '../../../hooks/useScrollToView';
import OrderOverview from './OrderOverview';
import EditInvoice from '../../../pages/EditInvoice/EditInvoice';
import TwDeleteInvoice from '../../TwDeleteInvoice/TwDeleteInvoice';
import useFormatUrlsInText from '../../../hooks/useFormatUrlText';
import { TabPanel } from '../../TwTabPanel/TwTabPanel';
import {
  AttachmentBasicProps,
  ORDER_INVOICE_STATUS,
  ORDER_STATUS,
  Order,
  OrderAttachment,
  SearchOrdersResponse
} from '../../../redux/api/orders/orders.model';
import TwUploadOrderTripFiles from '../../TwUploadOrderTripFiles/TwUploadOrderTripFiles';
import { TwSelectOption } from '../../TwSelects/TwSelects';
import {
  useDeleteOrderTripDocumentMutation,
  usePatchOrderMutation,
  useUpdateOrderStatusMutation,
  useUpdateOrderTripDocumentByIdMutation
} from '../../../redux/api/orders/ordersApi';
import TwDeleteOrderTripDoc from '../../TwUploadOrderTripFiles/TwDeleteOrderTripDoc';
import TwOrderTripsDocViewer from '../../TwOrderTripsDocViewer/TwOrderTripsDocViewer';
import { EntityType } from '../../../redux/Global.model';
import {
  closeLoadDetail,
  setLoadDetailReload,
  setTripDetailReload
} from '../../../redux/features/navigation/navigationSlice';
import { TRIPS_ITEMS_PER_PAGE, TRIP_STATUS, TripStop } from '../../../redux/api/trips/trips.model';
import { tripApi, tripApiUtil } from '../../../redux/api/trips/tripApi';
import { useLoadMenuOptions, useLTLOrdersMenuOptions } from '../../../hooks/useLoadMenu';
import { TwTripTooltipInfoProps } from '../../TwLoadTooltipInfo/TwLoadTooltipInfo';
import DateUtils from '../../../utils/dateUtils';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import InvoicesService from '../../../providers/Invoices/Invoices.service';
import FactoringService from '../../../providers/Factoring/Factoring.service';
import DocumentUtils from '../../../utils/documentUtils';
import TwPreviewDocument from '../../TwPreviewDocument/TwPreviewDocument';
import InvoiceUtils from '../../../utils/invoiceUtils';
import { CommodityList } from '../../../redux/api/commodities/commodities.model';
import { mapSavedOrderCommodityToCommodityList } from '../../../utils/commodity/commodityUtils';
import TwCommoditiesOverlayWrapper from '../../CommoditiesBuilder/TwCommoditiesOverlayWrapper/TwCommoditiesOverlayWrapper';

export interface OrderDetailProps {
  order: Order;
  primaryClient: ClientResponse | null;
  readonly?: boolean;
  forceOnlyEdit?: boolean;
  reloadData?: () => void;
  changeTab: (event: React.SyntheticEvent, newValue: string, isClickFromTab: boolean) => void;
  handleMarkers: (stops: TripStop[], startOrder?: number) => void;
}

const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB

const OrderTabDetail: React.FC<OrderDetailProps> = ({
  order,
  primaryClient,
  readonly,
  forceOnlyEdit,
  reloadData,
  changeTab,
  handleMarkers
}): JSX.Element => {
  const {
    data: { userData },
    getTenantSetting
  } = useAuth();
  const dispatch = useAppDispatch();
  const { addNotification } = useNotifications();
  const [scrollIntoView] = useScrollToView();
  const forUrlsInText = useFormatUrlsInText();
  const enableLtl = userData?.tenant?.ltl_enabled || false;

  const [loading, setLoading] = useState<boolean>(false);
  const dispatchNotesRef = React.useRef<HTMLDivElement>(null);
  const tripsFilterParams = useAppSelector((state) => state.activeTrips);
  const tripDetailReload = useAppSelector((state) => state.navigation.tripDetailReload);
  const isTripDetailOpen = useAppSelector((state) => state.navigation.isTripDetailOpen);
  const isOrderDetailOpen = useAppSelector((state) => state.navigation.isLoadDetailOpen);

  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const isRequireBolActive = getTenantSetting('require-bol');
  const isEnabledShowCustomerContact = getTenantSetting('show-customer-contact-on-dispatch');
  const isEnabledDriversSeeDocs = getTenantSetting('allow_drivers_to_view_documents');
  const use24HourClock = getTenantSetting('use-24-hour-clock', false);
  const showCustomerOrderAttachments = getTenantSetting('show-customer-order-attachments');

  const commoditiesEnabled =
    (userData?.tenantSettings && userData.tenantSettings['load-commodities']) || false;
  const { enableDynamicWebTwain, enableMarkAsDeliveredBtn, enableDocumentManager, enableCommoditiesDatabase } =
    useFeatureFlags();

  const isDriver = userData?.role === USER_ROLE.DRIVER;
  const isCarrier = userData?.role === USER_ROLE.CARRIER;
  const isCustomerContact = userData?.role === USER_ROLE.CUSTOMER_CONTACT;
  const isDriverOrCarrier = isDriver || isCarrier;
  const isAdminOrDispatcher =
    userData?.role === USER_ROLE.ADMIN ||
    userData?.role === USER_ROLE.OWNER ||
    userData?.role === USER_ROLE.DISPATCHER;

  const showCustomerContact =
    !isDriverOrCarrier || (isDriverOrCarrier && isEnabledShowCustomerContact);

  const [documentsOpen, setDocumentsOpen] = useState<boolean>(false);
  const [docViewOpen, setDocViewOpen] = useState(false);
  const [isTwScannerOpen, setIsTwScannerOpen] = useState<boolean>(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>('');
  const [selectedDocumentAttachment, setSelectedDocumentAttachment] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<OrderAttachment>();
  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [loadTabs, setLoadTabs] = useState<number>(0);

  // Edit invoice
  const [editInvoiceOpen, setEditInvoiceOpen] = useState<boolean>(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState<boolean>(false);
  const [viewInvoice, setViewInvoice] = useState<Blob | undefined>(undefined);
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState<boolean>(false);

  const [updateOrderTripDocumentById] = useUpdateOrderTripDocumentByIdMutation();
  const [deleteOrderTripDocument] = useDeleteOrderTripDocumentMutation();
  const [patchOrder] = usePatchOrderMutation();
  const [updateOrderStatus] = useUpdateOrderStatusMutation();

  // We get order by id in case Order tab is rendered in the Trips overlay
  const [orderData, setOrderData] = useState<Order | null>(order);

  const onChangeArchive = (loadChanged: SearchOrdersResponse | Order): void => {
    if (loadChanged.archived) {
      dispatch(closeLoadDetail());
      dispatch(setOrderId(undefined));
    }
  };

  const orderMenuOptions = enableLtl
    ? useLTLOrdersMenuOptions(order)
    : useLoadMenuOptions(order, onChangeArchive);

  const isDocumentVisible = (docVisibility: boolean | undefined): boolean =>
    !isDriver || docVisibility || false;

  const handleDocViewerOpen: any = (
    isDocVisible: boolean | undefined,
    docId: string,
    documentAttachmentId: string
  ): void => {
    if (isDocumentVisible(isDocVisible)) {
      setDocViewOpen(true);
      setSelectedDocumentId(docId);
      setSelectedDocumentAttachment(documentAttachmentId);
    }
  };

  const handleDocumentsUpload = async (): Promise<void> => {
    isTwScannerOpen && setIsTwScannerOpen(false);
    reloadData && reloadData();
  };

  const handleCloseDeleteDoc = (): void => {
    setShowDelete(false);
    setSelectedDocument(undefined);
  };

  const handleCloseEdit = (): void => {
    setShowEdit(false);
    setSelectedDocument(undefined);
  };

  const handleEditSuccess = (): void => {
    handleCloseEdit();
    reloadData && reloadData();
  };

  const handleDeleteDoc = (): void => {
    if (selectedDocument) {
      deleteOrderTripDocument({
        id: selectedDocument.id,
        orderId: order?.id,
        tripId: undefined,
        searchTripParams: tripsFilterParams,
        status: undefined // entityStatus
      })
        .unwrap()
        .then(() => {
          if (isOrderDetailOpen) {
            reloadData && reloadData();
          } else {
            dispatch(setTripDetailReload(true));
          }

          setShowDelete(false);
          setSelectedDocument(undefined);
        })
        .catch((e) => {
          addNotification({
            variant: 'error',
            message: e.message || 'Error deleting document'
          });
          setShowDelete(false);
          setSelectedDocument(undefined);
        });
    }
  };

  const handleDownloadDoc = async (doc: OrderAttachment): Promise<void> => {
    try {
      await DocumentUtils.handleOrderDownloadDoc(order, doc);
    } catch {
      addNotification({
        message: "Couldn't download attachment",
        variant: 'error'
      });
    }
  };

  const closeScanner = (documentUploaded: boolean, showNotification = true): void => {
    setIsTwScannerOpen(false);

    if (!documentUploaded) {
      logRocketTrack('Upload', {
        action: 'Upload document on full route tab',
        severity: 'high',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      if (showNotification) {
        addNotification({
          message: 'No document uploaded',
          position: 'bottomCenter',
          variant: 'error'
        });
      }
    }
  };

  const handleChangeUploadDocument = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e?.target?.files !== null) {
      if (e.target.files[0].size > MAX_FILE_SIZE) {
        addNotification({
          message: 'Error: maximum attachment size 15 mb',
          variant: 'error',
          position: 'bottomCenter'
        });
        return;
      }

      e.target.value = '';
      setDocumentsOpen(true);
    }
  };

  const handleLoadDetailsTabChange = (_event: React.SyntheticEvent, newValue: number): any => {
    setLoadTabs(newValue);
  };

  const handleDeleteInvoice = (): void => {
    reloadData && reloadData();
    setDeleteConfirmationOpen(false);

    addNotification({
      message: 'Invoice deleted successfully',
      variant: 'success'
    });
  };

  const handleSetHiddenFromDriver = (doc: OrderAttachment, driverVisibility: boolean): void => {
    if (doc.id) {
      updateOrderTripDocumentById({
        data: {
          visibility: driverVisibility,
          type: doc.type,
          name: doc.attachment.name,
          orderId: doc.order_id,
          tripId: doc.trip_id
        },
        id: doc.id
      })
        .unwrap()
        .then(() => {
          reloadData && reloadData();
        })
        .catch((e) => {
          addNotification({
            variant: 'error',
            message: e.message || 'Error updating document visibility'
          });
        });
    }
  };

  const handleSendToFactoring = async (invoiceId: string | null): Promise<void> => {
    if (!invoiceId) return;

    let invoice: InvoiceResponseV3 | null = null;
    try {
      invoice = (await InvoicesService.getInvoice(invoiceId)).data;
    } catch (error) {
      addNotification({
        message: 'Error fetching invoice',
        variant: 'error'
      });
    }

    if (!invoice) return;

    FactoringService.postExport(invoice)
      .then(() => {
        addNotification({
          message: 'Invoice sent to factoring',
          variant: 'success'
        });

        reloadData && reloadData();
      })
      .catch((error) => {
        console.error(error);

        const message = error.message
          ? error.message
          : error.error
          ? error.error
          : 'Error sending invoice to factoring. Contact support@truckbase.com';

        addNotification({
          message,
          variant: 'error'
        });
      });
  };

  const markAsDelivered = (): void => {
    if (!order) return;
    updateOrderStatus({
      id: order.id,
      status: ORDER_STATUS.DELIVERED,
      searchTripParams: { filters: tripsFilterParams?.filters }
    })
      .unwrap()
      .then((response) => {
        // Invalidate active trips to refresh the list
        // this is needed because the order status change could affect the trips
        // that are shown in the active trips list, so we need to refresh it since page 1 or offset 0
        dispatch(
          tripApi.endpoints.getActivePersistentTrips.initiate({
            filters: {
              ...tripsFilterParams?.filters,
              status: [TRIP_STATUS.DRAFT]
            },
            params: {
              sort_by: 'delivery_date',
              order_by: 'asc',
              limit: TRIPS_ITEMS_PER_PAGE,
              offset: 0
            }
          })
        );
        dispatch(
          tripApi.endpoints.getActivePersistentTrips.initiate({
            filters: {
              ...tripsFilterParams?.filters,
              status: [TRIP_STATUS.DISPATCHED]
            },
            params: {
              sort_by: 'delivery_date',
              order_by: 'asc',
              limit: TRIPS_ITEMS_PER_PAGE,
              offset: 0
            }
          })
        );
        dispatch(
          tripApi.endpoints.getActivePersistentTrips.initiate({
            filters: {
              ...tripsFilterParams?.filters,
              status: [TRIP_STATUS.IN_TRANSIT]
            },
            params: {
              sort_by: 'delivery_date',
              order_by: 'asc',
              limit: TRIPS_ITEMS_PER_PAGE,
              offset: 0
            }
          })
        );
        dispatch(
          tripApi.endpoints.getActivePersistentTrips.initiate({
            filters: {
              ...tripsFilterParams?.filters,
              status: [TRIP_STATUS.DELIVERED]
            },
            params: {
              sort_by: 'delivery_date',
              order_by: 'asc',
              limit: TRIPS_ITEMS_PER_PAGE,
              offset: 0
            }
          })
        );
        dispatch(tripApiUtil.invalidateTags(['ActiveTrips']));
        addNotification({
          message: 'Order marked as delivered',
          variant: 'success'
        });
        dispatch(setLoadDetailReload(true));
      })
      .catch((error) => {
        console.error(error);
        addNotification({
          message: error.message || 'Failed to mark as delivered, please try again',
          variant: 'error'
        });
      });
  };

  const markAsReadyForInvoicing = (): void => {
    if (!order) return;

    const tripFilters = { ...tripsFilterParams?.filters, includeArchived: matchesMobileOrTablet };

    patchOrder({
      id: order.id,
      data: { invoice_status: ORDER_INVOICE_STATUS.readyForInvoicing },
      searchTripParams: { filters: tripFilters }
    })
      .unwrap()
      .then((response) => {
        // Invalidate active trips to refresh the list
        // this is needed because the order status change could affect the trips
        // that are shown in the active trips list, so we need to refresh it since page 1 or offset 0
        dispatch(
          tripApi.endpoints.getActivePersistentTrips.initiate({
            filters: {
              ...tripFilters,
              status: [TRIP_STATUS.DRAFT]
            },
            params: {
              sort_by: 'delivery_date',
              order_by: 'asc',
              limit: TRIPS_ITEMS_PER_PAGE,
              offset: 0
            }
          })
        );
        dispatch(
          tripApi.endpoints.getActivePersistentTrips.initiate({
            filters: {
              ...tripFilters,
              status: [TRIP_STATUS.DISPATCHED]
            },
            params: {
              sort_by: 'delivery_date',
              order_by: 'asc',
              limit: TRIPS_ITEMS_PER_PAGE,
              offset: 0
            }
          })
        );
        dispatch(
          tripApi.endpoints.getActivePersistentTrips.initiate({
            filters: {
              ...tripFilters,
              status: [TRIP_STATUS.IN_TRANSIT]
            },
            params: {
              sort_by: 'delivery_date',
              order_by: 'asc',
              limit: TRIPS_ITEMS_PER_PAGE,
              offset: 0
            }
          })
        );
        dispatch(
          tripApi.endpoints.getActivePersistentTrips.initiate({
            filters: {
              ...tripFilters,
              status: [TRIP_STATUS.DELIVERED]
            },
            params: {
              sort_by: 'delivery_date',
              order_by: 'asc',
              limit: TRIPS_ITEMS_PER_PAGE,
              offset: 0
            }
          })
        );
        dispatch(tripApiUtil.invalidateTags(['ActiveTrips']));
        addNotification({
          message: 'Order marked as Ready for Invoicing',
          variant: 'success'
        });

        dispatch(setLoadDetailReload(true));
      })
      .catch((error) => {
        console.error(error);
        addNotification({
          message: error.message || 'Failed to mark as Ready for Invoicing, please try again',
          variant: 'error'
        });
      });
  };

  const getNewCommodities = (): CommodityList[] => {
    if (isTripDetailOpen && orderData) {
      return orderData?.order_commodities?.map((commodity) => {
        return mapSavedOrderCommodityToCommodityList(commodity);
      }) || [];
    } else {
      return order?.order_commodities?.map((commodity) => {
        return mapSavedOrderCommodityToCommodityList(commodity);
      }) || [];
    }
  };

  useEffect(() => {
    if (!open) {
      setOrderData(null);
    }
  }, [open]);

  useEffect(() => {
    if (isOrderDetailOpen) {
      // locally this was crashing after creating a new order from the trips tab so added check to see that function exists though it appears to where instantiated so not sure what's up -LR
      handleMarkers &&
        handleMarkers(enableLtl ? order.stops : order.full_route_stop_overview || [], 1);
    }
  }, [order]);

  useEffect(() => {
    // Getting order data from the order id to get extra data we need
    (async (): Promise<void> => {
      if ((order?.id && isTripDetailOpen) || tripDetailReload) {
        setLoading(true);

        try {
          const orderData = await LoadsService.getOrderById(order.id);
          setOrderData(orderData);
          handleMarkers(orderData.stops, 1);
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
          dispatch(setTripDetailReload(false));
        }
      }
    })();
  }, [order?.id, isTripDetailOpen, tripDetailReload]);

  // Return an array of tooltip data for all the trips related to the Order
  const tripsTooltipDataArray = useMemo(() => {
    if (!order && !orderData) return [];

    // If we are in Trips overlay, we have to use ordersData to get the Order full trip data
    const orderTrips = isTripDetailOpen ? orderData?.trips || [] : order.trips || [];

    return orderTrips?.map((trip) => {
      const pickup = trip?.stops ? trip?.stops[0] : null;
      const delivery = trip?.stops ? trip?.stops[trip.stops.length - 1] : null;

      const tooltipData: TwTripTooltipInfoProps = {
        loadId: '',
        tripId: trip.id,
        tags: order?.tags || [],
        tripStatus: trip.status || TRIP_STATUS.DRAFT,
        clientName: order.raw_customer?.name || '',
        pickupLocation: trip.stops ? trip.stops[0].location : null,
        deliveryLocation: trip.stops ? trip.stops[trip.stops.length - 1].location : null,
        stops: trip.stops ? trip.stops.length - 2 : 0,
        pickupDate:
          pickup && pickup.start_date
            ? DateUtils.getMediumDate(new Date(pickup.start_date)).split(',')[0]
            : '',
        pickupStartTime: pickup
          ? DateUtils.formatTimeForTooltip(use24HourClock, pickup.start_time, pickup.start_date)
          : '',
        pickupEndTime: pickup
          ? DateUtils.formatTimeForTooltip(use24HourClock, pickup.end_time, pickup.start_date)
          : '',
        deliveryDate:
          delivery && delivery.end_date
            ? DateUtils.getMediumDate(new Date(delivery?.end_date)).split(',')[0]
            : '',
        deliveryStartTime: delivery
          ? DateUtils.formatTimeForTooltip(use24HourClock, delivery.start_time, delivery.end_date)
          : '',
        deliveryEndTime: delivery
          ? DateUtils.formatTimeForTooltip(use24HourClock, delivery.end_time, delivery.end_date)
          : '',
        unassigned: !!(trip.drivers && trip.drivers.length === 0),
        loadReadableId: order.readable_id || '',
        tripReadableId: trip.readable_id,
        pickupNumber:
          trip.stops && trip.stops.length > 0 ? trip.stops[0].stop_identifier || '' : '',
        deliveryNumber:
          trip.stops && trip.stops.length > 0
            ? trip.stops[trip.stops.length - 1].stop_identifier || ''
            : '',
        segmentsCount: orderTrips?.length || 0,
        order: trip.sort_order || 0,
        driverName:
          trip.drivers && trip.drivers.length > 0
            ? `${trip.drivers[0]?.first_name} ${trip.drivers[0].last_name}`
            : '',
        truckName: trip.truck?.name || '',
        trailerName: trip.trailer?.name || ''
      };

      return tooltipData;
    });
  }, [JSON.stringify(order.trips), JSON.stringify(orderData?.trips), isTripDetailOpen]);

  useEffect(() => {
    if (loadTabs === 1 && matchesMobileOrTablet) {
      scrollIntoView(dispatchNotesRef, 'start');
    }
  }, [loadTabs, matchesMobileOrTablet]);

  const filesToPreview: OrderAttachment[] = useMemo(() => {
    if (isDriver) {
      return order?.order_attachments?.filter((doc) => doc.visibility) ?? [];
    } else if (isCustomerContact && showCustomerOrderAttachments) {
      return order?.order_attachments?.filter((doc) => doc.order_id) ?? [];
    } else {
      return order?.order_attachments ?? [];
    }
  }, [isDriver, order?.order_attachments]);

  const generatedFiles = useMemo(() => {
    if (!order) return [];

    const generatedFiles = order?.order_attachments?.filter((doc) => doc.is_generated) || [];

    return generatedFiles;
  }, [order?.order_attachments]);

  const tripOptionsForUpload = useMemo(() => {
    let options = [] as any[];

    if (orderData && orderData.trips?.length > 0) {
      options = orderData.trips?.map((trip) => {
        return {
          value: trip.id || '',
          label: enableLtl
            ? `${trip.readable_id}`
            : `${order.readable_id} - ${trip.sort_order + 1}`,
          customer: enableLtl
            ? `${trip.readable_id}`
            : `${order.readable_id} - ${trip.sort_order + 1}`
        };
      });
    }

    options.push({
      value: '',
      label: '-',
      customer: 'Do not assign trip'
    });

    return options;
  }, [orderData?.trips]);

  const showCommodities = useMemo(() => {
    if (!commoditiesEnabled) return false;

    if (isTripDetailOpen && orderData) {
      return orderData?.order_commodities?.length > 0;
    } else {
      return order?.order_commodities?.length > 0;
    }
  }, [orderData, order, commoditiesEnabled, isTripDetailOpen]);

  return (
    <>
      <Box component="section" className="load-detail__sub-header">
        <Box component="div" className="load-detail__sub-header__user-info">
          <TwInitialsIndicator
            text={(primaryClient?.initials ?? primaryClient?.name) || ''}
            displayRawText={!primaryClient?.initials}
          />
          <Box
            component="div"
            className="load-detail__sub-header__user-info--data"
            sx={{
              justifyContent: 'center'
            }}
          >
            <Box component="span">{primaryClient?.name}</Box>
          </Box>
        </Box>

        <Box component="div" className="load-detail__sub-header__btn-container">
          {!isDriverOrCarrier && (
            <>
              {!readonly &&
                enableMarkAsDeliveredBtn &&
                (order?.status === ORDER_STATUS.NOT_STARTED ||
                  order?.status === ORDER_STATUS.IN_TRANSIT) && (
                  <TwButton
                    label="Mark as delivered"
                    type={TwButtonType.primary}
                    action={markAsDelivered}
                  />
                )}

              {!readonly &&
                order?.status === ORDER_STATUS.DELIVERED &&
                order.invoice_status === ORDER_INVOICE_STATUS.notInvoiced && (
                  <TwButton
                    label="Mark as ready for invoicing"
                    type={TwButtonType.primary}
                    action={markAsReadyForInvoicing}
                  />
                )}

              {(!readonly || forceOnlyEdit) && (
                <Box
                  component="div"
                  className="load-detail__sub-header__btn-container--menu-container"
                >
                  <TwLoadMenu
                    options={forceOnlyEdit ? [orderMenuOptions[0]] : orderMenuOptions}
                    paperClassName="load-menu"
                    buttonClassName="load-menu-btn"
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {matchesMobileOrTablet && !isCustomerContact && (
        <Box component="section" className="sub-header">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {matchesMobileOrTablet &&
              (enableDynamicWebTwain ? (
                <TwButton
                  type={TwButtonType.secondary}
                  label={'Upload docs or photos'}
                  action={(): void => setIsTwScannerOpen(true)}
                  fullwidth={matchesMobileOrTablet}
                  className={'dynamicwt-button'}
                />
              ) : (
                <>
                  <input
                    type="file"
                    id="upload"
                    accept="application/pdf image/png image/jpeg image/heic"
                    hidden
                    onChange={handleChangeUploadDocument}
                  />
                  <label
                    style={{
                      // if you're seeing this please move to a css file, I'm putting here because I can't get it to work otherwise :( thx, Lisa
                      marginTop: '12px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      boxSizing: 'border-box',
                      height: '40px',
                      background: '#ffffff',
                      border: '1px solid #d1d5db',
                      boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
                      borderRadius: '6px',
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '20px',
                      margin: '0 auto',
                      width: '100%'
                    }}
                    htmlFor="upload"
                  >
                    Upload document
                  </label>
                </>
              ))}
          </Box>
        </Box>
      )}

      <Box component="section" className="load-detail__data-container">
        <Box component="div" className="load-detail__data-container__data">
          <TwLoadDetailTabsGroup
            value={loadTabs}
            onChange={handleLoadDetailsTabChange}
            aria-label="TwTabs"
            role="TwTabs"
          >
            <TwLoadDetailTab label="Order overview" />
            {!isCustomerContact && !isDriverOrCarrier && (
              <TwLoadDetailTab
                label="Notes"
                icon={<Chip sx={{ height: '20px' }} label={order?.dispatch_note_count || 0} />}
                iconPosition="end"
              />
            )}
          </TwLoadDetailTabsGroup>
          <TabPanel id="load-detail-tab" value={loadTabs} index={0}>
            <>
              <Box component="div" className="load-detail__data-container__data--title">
                Route
              </Box>
              {order && (
                <RouteInfo
                  load={isTripDetailOpen && orderData ? orderData : order}
                  showStatusUpdateInput={true}
                  showStatusReadOnly
                  showAddSignatureAction={false}
                  isCustomerLink={isCustomerContact}
                  onSignatureSaved={(): void => {
                    reloadData && reloadData();
                  }}
                  showCheckInAndOutTimes={false}
                  tripsTooltipDataArray={tripsTooltipDataArray}
                />
              )}
              {showCommodities && !enableCommoditiesDatabase && (
                <TwLoadCommodityReadOnly
                  commodities={
                    isTripDetailOpen && orderData
                      ? orderData?.order_commodities
                      : order?.order_commodities
                  }
                  entity={EntityType.ORDER}
                />
              )}

              {showCommodities && enableCommoditiesDatabase && (
                <TwCommoditiesOverlayWrapper commodities={getNewCommodities()} />
              )}

              {!isCustomerContact && (
                <>
                  <Box component="div" className="load-detail__data-container__data--title">
                    Order notes
                  </Box>
                  <Box
                    component="div"
                    className="load-detail__data-container__data__special-instructions"
                  >
                    {order && forUrlsInText(order.instructions)}
                  </Box>
                  <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    spacing={1}
                  >
                    <Box component="div" className="load-detail__data-container__data--title">
                      Attachments
                    </Box>
                    {isRequireBolActive && !matchesMobileOrTablet && order && order.bol_missing && (
                      <TwWarningIcon message="POD is missing" />
                    )}
                  </Stack>

                  <Box component="div" className="load-detail__data-container__data__attachments">
                    <Stack
                      className="load-detail__data-container__data__attachments--container-2"
                      onClick={(): void => {
                        if (matchesMobileOrTablet && enableDynamicWebTwain) {
                          setIsTwScannerOpen(true);
                        } else {
                          setDocumentsOpen(true);
                        }
                      }}
                    >
                      <p className="load-detail__data-container__data__attachments--text">
                        <CloudUploadIcon
                          className="load-detail__data-container__data__attachments--icon"
                          sx={{ marginRight: '10px' }}
                        />
                        Click here to{' '}
                        <span className="load-detail__data-container__data__attachments--highlited-text">
                          upload a file
                        </span>
                      </p>
                      <p className="load-detail__data-container__data__attachments--secondaryText">
                        Files Supported: PDF, XSLS, JPG, PNG, maximum size 200MB{' '}
                      </p>
                    </Stack>

                    {order &&
                      order.order_attachments
                        ?.filter((doc) => !doc.is_generated)
                        .map((document) => (
                          <DetailFile
                            key={document.id}
                            onClick={(): void => {
                              handleDocViewerOpen(
                                document.visibility,
                                document.id,
                                document.document_attachment_id
                              );
                            }}
                            fileName={document.attachment.name}
                            type={StringUtils.getLoadDocumentTypeLabel(document.type)}
                            created_at={
                              document.created_at ? new Date(document.created_at) : undefined
                            }
                            onEdit={(): void => {
                              setShowEdit(true);
                              setSelectedDocument(document);
                            }}
                            onDelete={(): void => {
                              setShowDelete(true);
                              setSelectedDocument(document);
                            }}
                            onDownload={(): void => {
                              handleDownloadDoc(document);
                            }}
                            showMenu={isDriverOrCarrier ? false : true}
                            driverVisibility={document.visibility}
                            size={document.attachment.size}
                            onVisibilityMenu={(visible: boolean): void => {
                              handleSetHiddenFromDriver(document, visible);
                            }}
                            showVisibility={true}
                            isEncrypted={document.is_encrypted}
                            fileType={document.attachment.file_type}
                            fileKey={document.attachment.key}
                            tag={
                              document.trip_tag
                                ? enableLtl
                                  ? `Trip #${document.trip_tag.readable_id}`
                                  : `Trip #${order.readable_id}-${
                                      document.trip_tag.trip_sort_order + 1
                                    }`
                                : undefined
                            }
                            attachmentId={document.id}
                          />
                        ))}
                  </Box>
                  {!isDriverOrCarrier && order && (
                    <TwGenerateDocuments
                      files={generatedFiles}
                      loadId={order.id}
                      isBrokeredLoad={order.is_brokered_order}
                      onClick={(id): void => {
                        handleDocViewerOpen(true, id, null);
                      }}
                      onSuccessfulGenerate={reloadData}
                      onDelete={(file): void => {
                        if (file) {
                          setShowDelete(true);
                          setSelectedDocument(file);
                        }
                      }}
                      onDownload={(file): void => {
                        handleDownloadDoc(file);
                      }}
                      invoice={order.invoice}
                      divisionId={order.division_id || ''}
                      onEditInvoice={(): void => {
                        setEditInvoiceOpen(true);
                      }}
                      onDeleteInvoice={(): void => {
                        setDeleteConfirmationOpen(true);
                      }}
                      handleSendToFactoring={handleSendToFactoring}
                    />
                  )}
                </>
              )}
              {isCustomerContact && showCustomerOrderAttachments && (
                <Box>
                  <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    spacing={1}
                  >
                    <Box component="div" className="load-detail__data-container__data--title">
                      Attachments
                    </Box>
                  </Stack>
                  {order &&
                    order.order_attachments
                      ?.filter((doc) => !doc.is_generated && doc.order_id)
                      .map((document) => (
                        <DetailFile
                          key={document.id}
                          onClick={(): void => {
                            handleDocViewerOpen(
                              document.visibility,
                              document.id,
                              document.attachment.id
                            );
                          }}
                          fileName={document.attachment.name}
                          type={StringUtils.getLoadDocumentTypeLabel(document.type)}
                          created_at={
                            document.created_at ? new Date(document.created_at) : undefined
                          }
                          onDownload={(): void => {
                            handleDownloadDoc(document);
                          }}
                          showMenu={false}
                          size={document.attachment.size}
                          showVisibility={false}
                          isEncrypted={document.is_encrypted}
                          fileType={document.attachment.file_type}
                          fileKey={document.attachment.key}
                          attachmentId={document.id}
                        />
                      ))}
                </Box>
              )}
            </>
          </TabPanel>
          {!isDriverOrCarrier && (
            <TabPanel id="dispatch-notes-tab" value={loadTabs} index={1}>
              <TwDispatchNotes
                orderId={order?.id || ''}
                isSplitLoad={true}
                reloadData={(): void => {
                  dispatch(setTripDetailReload(true));
                }}
                loadReadableId={order?.readable_id || 0}
                componentRef={dispatchNotesRef}
                ltlEnabled={enableLtl}
              />
            </TabPanel>
          )}
        </Box>
        {matchesMobileOrTablet && loadTabs === 1 ? (
          <></>
        ) : (
          <OrderOverview
            changeTab={changeTab}
            loadData={isTripDetailOpen ? orderData : order}
            reloadData={reloadData}
          />
        )}
      </Box>

      <TwUploadOrderTripFiles
        open={documentsOpen}
        orderId={order?.id || ''}
        entity="order"
        defaultVisibility={isDriver ? true : isEnabledDriversSeeDocs}
        handleClose={(): void => setDocumentsOpen(false)}
        entityList={tripOptionsForUpload}
        onAddDocumentsSuccess={(): void => {
          if (isTripDetailOpen) {
            dispatch(setTripDetailReload(true));
          } else {
            reloadData && reloadData();
          }
        }}
        disableInternalUpload={false}
      />

      <TwDeleteOrderTripDoc
        open={showDelete}
        fileName={selectedDocument?.attachment.name || ''}
        onClickClose={handleCloseDeleteDoc}
        onDelete={handleDeleteDoc}
      />

      <EditFile
        open={showEdit}
        document={selectedDocument}
        onClickCLose={handleCloseEdit}
        onEditSuccess={handleEditSuccess}
        entity="order"
        entityList={tripOptionsForUpload}
        order={{ id: order?.id || '', readable_id: order?.readable_id || '' }}
      />

      {matchesMobileOrTablet && enableDynamicWebTwain && order && (
        <TwScanner
          isOpen={isTwScannerOpen}
          closeMenu={(): void => setIsTwScannerOpen(false)}
          onClose={closeScanner}
          onUploadSuccess={handleDocumentsUpload}
          orderId={order.id}
          entity="order"
          entityList={tripOptionsForUpload}
          defaultVisibility={isDriver ? true : isEnabledDriversSeeDocs}
          ltlEnabled={enableLtl}
        />
      )}

      {/** Document and photo preview */}
      {order &&
        order.order_attachments &&
        (!isAdminOrDispatcher ||
          matchesMobileOrTablet ||
          !enableDocumentManager ||
          !selectedDocumentAttachment) && (
          <TwOrderTripsDocViewer
            readableId={order?.readable_id}
            selectedFileId={selectedDocumentId}
            files={filesToPreview}
            open={docViewOpen}
            onClickClose={(): void => {
              setDocViewOpen(false);
              setSelectedDocumentId('');
            }}
            onDelete={(doc: OrderAttachment): void => {
              setDocViewOpen(false);
              setSelectedDocument(doc);
              setShowDelete(true);
            }}
          />
        )}

      {/* document viewer for admin and dispatcher */}
      {docViewOpen &&
        isAdminOrDispatcher &&
        selectedDocumentAttachment &&
        !matchesMobileOrTablet &&
        enableDocumentManager && (
          <TwPreviewDocument
            open={docViewOpen}
            files={filesToPreview.map((file) => {
              return {
                id: file.document_attachment_id,
                attachment_id: file.attachment_id,
                name: file.attachment.name,
                size: file.attachment.size,
                file_type: file.attachment.file_type,
                key: file.attachment.key,
                s3_key: file.attachment.s3_key,
                s3_signed_url: file.attachment.s3_signed_url,
                s3_signed_download_url: file.attachment.s3_signed_download_url,
                created_at: file.created_at
              } as AttachmentBasicProps;
            })}
            id={selectedDocumentAttachment}
            isReview={false}
            onClose={(): void => setDocViewOpen(false)}
            reloadData={reloadData}
          />
        )}

      {order && order.invoice && (
        <>
          <TwDocumentViewer
            readableId={order.invoice.invoice_id || ''}
            files={null}
            blob={viewInvoice}
            blobType={TwDocumentType.pdf}
            blobTitle={InvoiceUtils.getInvoiceBlobTitle(order.invoice.invoice_id || '')}
            downloadName={InvoiceUtils.getInvoiceDownloadName(order.invoice.invoice_id || '')}
            hideCloseIcon={order.invoice.source === InvoiceSources.QBO}
            open={invoicePreviewOpen}
            onClickClose={(): void => {
              setViewInvoice(undefined);
              setInvoicePreviewOpen(false);
            }}
          />

          <EditInvoice
            invoiceId={order.invoice.id || ''}
            divisionId={order.invoice.division_id || ''}
            open={editInvoiceOpen}
            setEditInvoiceOpen={setEditInvoiceOpen}
            setViewInvoiceBlob={(blob): void => setViewInvoice(blob)}
            onClickClose={(): void => {
              dispatch(setOrderId(order.id));
              setEditInvoiceOpen(false);
            }}
            setReloadInvoices={(): void => undefined}
            setViewInvoiceOpen={(): void => {
              setInvoicePreviewOpen(true);
            }}
            onDeleteInvoiceFromPreview={handleDeleteInvoice}
          />

          <TwDeleteInvoice
            invoiceId={order.invoice?.id || ''}
            open={deleteConfirmationOpen}
            onCancel={(): void => setDeleteConfirmationOpen(false)}
            onDelete={handleDeleteInvoice}
          />
        </>
      )}

      {loading && (
        <Backdrop
          className="load-detail__trip-loader"
          sx={{
            color: '#fff',
            position: 'absolute',
            zIndex: 10000,
            top: 0,
            right: 0
          }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </>
  );
};

export default OrderTabDetail;
